/* =====================================================================
   Superette × Fish Wishlist v2 — move items between lists without opening them (v1.2, Wishlist V3)
   ⚠ JavaScript. Never paste into the Fish Wishlist Custom CSS box.
   · Every closed list card (your lists + Your Waitlist) shows ALL its products in one slim row that
     scrolls sideways (swipe / trackpad / arrows), instead of Fish's first 4 + "+N".
   · Same size products as Fish's own row (it reuses Fish's .fw2-thumbs / .fw2-thumb styles).
   · Desktop: drag a product — from a closed list's row OR an open list — onto another list card →
     moved, "Moved to … · Undo".
     The drawer scrolls itself while you drag near its top or bottom edge.
   · Mobile / touch: long-press a product (closed row or open list) → "Move to…" sheet.
   · v1.2 (V3): tap a product in a closed row → its product page (long-press still moves it).
     Quick add on every product in the row: touch = the white + square bottom right, desktop = "Add To Bag"
     sliding up on hover — adds the saved size to the bag straight away and opens the Bag drawer.
   Your Waitlist and Buy it again can't be dragged into or out of (they're sign-ups / past orders).
   ===================================================================== */
(() => {
  const SKIP = ['WL10', 'WL12']; // Waitlist (Notify Me), Buy it again
  const STRIP_LISTS_EXTRA = ['WL10']; // the Waitlist gets the scrolling row, but no moving
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const waitFor = async (fn, ms) => { const end = Date.now() + ms; while (Date.now() < end) { if (fn()) return true; await wait(100); } return false; };
  const clean = (v) => String(v || '').replace(/[^\d]/g, '');
  const lists = () => (window.FishWishlist && window.FishWishlist.wishlists) || {};
  const items = (id) => (Array.isArray(lists()[id]) ? lists()[id] : []);
  const has = (id, vid) => items(id).some((x) => clean(x && x.id) === clean(vid));
  const fine = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const ownCards = (drawer) => [...drawer.querySelectorAll('.fw2-pane--lists fw2-list-card[data-id]')]
    .filter((c) => !c.closest('.fw2-buy-again-slot--lists'));
  const movable = (card) => card && !SKIP.includes(card.dataset.id);
  const nameOf = (id) => {
    const n = document.querySelector(`fw2-drawer fw2-list-card[data-id="${id}"] .fw2-card__name`);
    return (n && n.textContent.trim()) || 'your list';
  };

  /* ---------- add to / remove from one list, no drawer (same hidden Fish button as the rest of the build) ---------- */
  let queue = Promise.resolve();
  const fishToggle = (variantId, listId, want) => (queue = queue.then(async () => {
    const id = clean(variantId);
    const wrap = document.createElement('div');
    wrap.innerHTML = `<wishlist-button-pdp data-wishlist-id="${String(listId).replace(/[^\w-]/g, '')}" role="button" class="fish-wishlist-button--pdp sp-move-fish" aria-hidden="true" tabindex="-1" data-variant-id="${id}" data-preselected-id="${id}" data-is-added="false"></wishlist-button-pdp>`;
    const fish = wrap.firstElementChild;
    document.body.appendChild(fish);
    const wanted = want === 'add' ? 'true' : 'false';
    try {
      await wait(600);
      if (fish.dataset.isAdded === wanted) return true;
      fish.click();
      const ok = await waitFor(() => fish.dataset.isAdded === wanted, 4000);
      await wait(150);
      document.querySelectorAll('atw-drawer').forEach((d) => { if (d.dataset.open === 'true' && typeof d.close === 'function') d.close(); });
      return !!ok;
    } catch (e) { return false; } finally { fish.remove(); }
  }));

  /* ---------- toast (shares the build's toast) ---------- */
  let toastTimer;
  const toast = (text, actionLabel, action) => {
    let el = document.querySelector('.sp-wl-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'sp-wl-toast';
      el.setAttribute('role', 'status');
      el.innerHTML = '<span class="sp-wl-toast__text"></span><button type="button" class="sp-wl-toast__action"></button>';
      document.body.appendChild(el);
    }
    el.querySelector('.sp-wl-toast__text').textContent = text;
    const btn = el.querySelector('.sp-wl-toast__action');
    btn.textContent = actionLabel || '';
    btn.hidden = !actionLabel;
    btn.onclick = () => { el.classList.remove('is-visible'); action && action(); };
    setTimeout(() => el.classList.add('is-visible'), 20);
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 5000);
  };

  /* ---------- the move ---------- */
  let busy = false;
  const move = async (vid, from, to, undoing) => {
    if (busy || !vid || from === to) return;
    busy = true;
    const root = document.documentElement;
    root.dataset.spMoving = 'true';
    const already = has(to, vid);
    document.querySelectorAll(`.sp-strip__item[data-vid="${clean(vid)}"][data-list="${from}"]`).forEach((n) => n.classList.add('is-moving'));
    try {
      const added = already || (await fishToggle(vid, to, 'add'));
      const removed = added && (await fishToggle(vid, from, 'remove'));
      if (!added || !removed) { toast('Couldn’t move that item. Please try again.'); return; }
      if (undoing) toast(`Moved back to ${nameOf(to)}`);
      else toast(`Moved to ${nameOf(to)}`, 'Undo', () => move(vid, to, from, true));
    } finally {
      setTimeout(() => { delete root.dataset.spMoving; }, 500);
      busy = false;
      setTimeout(refresh, 300);
      setTimeout(refresh, 1500);
    }
  };

  /* ---------- the scrolling row on each closed card ---------- */
  const sig = (id) => items(id).map((x) => clean(x && x.id)).join(',');
  const img = (v) => {
    const f = v && v.featured_image;
    return (f && (f.src_large || f.src)) || (v && v.image) || '';
  };
  const renderStrip = (card) => {
    const id = card.dataset.id;
    const box = card.querySelector('.fw2-card__collapsed');
    if (!box) return;
    const s = sig(id);
    let strip = box.querySelector(':scope > .sp-strip');
    if (!s) { if (strip) strip.remove(); card.classList.remove('sp-has-strip'); return; }
    if (strip && strip.dataset.sig === s) return;
    const drag = movable(card) && fine();
    const html = items(id).map((v) => {
      const vid = clean(v && v.id);
      const name = (v && (v.name || v.title)) || '';
      const url = (v && v.url) || '';
      const add = v && v.available !== false && id !== 'WL10' ? `<button type="button" class="sp-strip__add" aria-label="Add ${esc(name)} to bag"></button>` : '';
      return `<div class="fw2-thumb sp-strip__item" role="link" tabindex="0" data-vid="${vid}" data-list="${esc(id)}" data-url="${esc(url)}"${drag ? ' draggable="true"' : ''} aria-label="${esc(name)}" title="${esc(name)}"><img src="${esc(img(v))}" alt="" loading="lazy" draggable="false">${add}</div>`;
    }).join('');
    if (!strip) {
      strip = document.createElement('div');
      strip.className = 'sp-strip';
      strip.innerHTML = '<button type="button" class="sp-strip__arrow sp-strip__arrow--prev" aria-label="Scroll left" tabindex="-1"></button><div class="fw2-thumbs sp-strip__track"></div><button type="button" class="sp-strip__arrow sp-strip__arrow--next" aria-label="Scroll right" tabindex="-1"></button>';
      box.appendChild(strip);
      const track = strip.querySelector('.sp-strip__track');
      track.addEventListener('scroll', () => arrows(strip), { passive: true });
      strip.querySelector('.sp-strip__arrow--prev').addEventListener('click', (e) => { e.stopPropagation(); track.scrollBy({ left: -track.clientWidth * 0.8, behavior: 'smooth' }); });
      strip.querySelector('.sp-strip__arrow--next').addEventListener('click', (e) => { e.stopPropagation(); track.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' }); });
    }
    strip.dataset.sig = s;
    strip.querySelector('.sp-strip__track').innerHTML = html;
    card.classList.add('sp-has-strip');
    requestAnimationFrame(() => arrows(strip));
  };
  const arrows = (strip) => {
    const t = strip.querySelector('.sp-strip__track');
    strip.classList.toggle('can-prev', t.scrollLeft > 4);
    strip.classList.toggle('can-next', t.scrollLeft + t.clientWidth < t.scrollWidth - 4);
  };
  // open lists: each product row can be dragged too (desktop)
  const rows = (card) => {
    const on = movable(card) && fine();
    card.querySelectorAll('fw2-item-row[data-id]').forEach((r) => {
      if (on && r.getAttribute('draggable') !== 'true') r.setAttribute('draggable', 'true');
      r.classList.toggle('sp-row-movable', movable(card));
    });
  };
  const refresh = () => {
    document.querySelectorAll('fw2-drawer').forEach((d) => ownCards(d)
      .filter((c) => movable(c) || STRIP_LISTS_EXTRA.includes(c.dataset.id))
      .forEach((c) => { renderStrip(c); rows(c); }));
  };
  // the thing being picked up: a product in a closed row, or a product row in an open list
  const source = (el) => {
    const it = el.closest && el.closest('fw2-drawer .sp-strip__item, fw2-drawer fw2-item-row.sp-row-movable[data-id]');
    if (!it) return null;
    if (it.classList.contains('sp-strip__item')) return { el: it, vid: it.dataset.vid, from: it.dataset.list, name: it.getAttribute('aria-label') || '' };
    const card = it.closest('fw2-list-card[data-id]');
    if (!movable(card)) return null;
    const title = it.querySelector('.fw2-row__title');
    return { el: it, vid: clean(it.dataset.id), from: it.dataset.wishlistId || card.dataset.id, name: (title && title.textContent.trim()) || '' };
  };

  /* ---------- desktop: drag and drop ---------- */
  let dragged = null;
  let scroller = null;
  let scrollTick = null;
  const scrollParent = (el) => {
    for (let n = el; n && n !== document.body; n = n.parentElement) {
      const o = getComputedStyle(n).overflowY;
      if ((o === 'auto' || o === 'scroll') && n.scrollHeight > n.clientHeight) return n;
    }
    return null;
  };
  const clearOver = () => document.querySelectorAll('fw2-list-card.sp-drop-over').forEach((c) => c.classList.remove('sp-drop-over'));
  document.addEventListener('dragstart', (e) => {
    if (e.target.closest && e.target.closest('button, a, select, input, .fw2-row__actions')) {
      if (!e.target.closest('.sp-strip__item')) return; // let buttons inside an open row behave normally
    }
    const src = source(e.target);
    if (!src || src.el.getAttribute('draggable') !== 'true') return;
    const it = src.el;
    dragged = { vid: src.vid, from: src.from };
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', src.vid); } catch (err) { /* ignore */ }
    const im = it.querySelector('img');
    if (im && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(im, im.width / 2, im.height / 2);
    const drawer = it.closest('fw2-drawer');
    drawer.dataset.spDragging = 'true';
    scroller = scrollParent(it.closest('.fw2-pane--lists') || drawer);
    it.classList.add('is-dragging');
  });
  document.addEventListener('dragover', (e) => {
    if (!dragged) return;
    const card = e.target.closest && e.target.closest('fw2-drawer fw2-list-card[data-id]');
    const ok = card && movable(card) && !card.closest('.fw2-buy-again-slot--lists') && card.dataset.id !== dragged.from;
    clearOver();
    if (ok) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; card.classList.add('sp-drop-over'); }
    // auto-scroll near the drawer's top / bottom edge
    if (scroller) {
      const r = scroller.getBoundingClientRect();
      const edge = 70;
      const dy = e.clientY < r.top + edge ? -12 : e.clientY > r.bottom - edge ? 12 : 0;
      clearInterval(scrollTick);
      if (dy) scrollTick = setInterval(() => scroller.scrollBy(0, dy), 16);
    }
  });
  document.addEventListener('drop', (e) => {
    if (!dragged) return;
    const card = e.target.closest && e.target.closest('fw2-drawer fw2-list-card[data-id]');
    if (card && movable(card) && card.dataset.id !== dragged.from && !card.closest('.fw2-buy-again-slot--lists')) {
      e.preventDefault();
      move(dragged.vid, dragged.from, card.dataset.id);
    }
  });
  document.addEventListener('dragend', () => {
    clearInterval(scrollTick);
    clearOver();
    document.querySelectorAll('fw2-drawer[data-sp-dragging]').forEach((d) => delete d.dataset.spDragging);
    document.querySelectorAll('fw2-drawer .is-dragging').forEach((n) => n.classList.remove('is-dragging'));
    dragged = null;
  });

  /* ---------- touch: long-press → "Move to…" sheet ---------- */
  let press = null;
  let suppressClick = false;
  const closeSheet = () => document.querySelectorAll('.sp-move-sheet').forEach((s) => { s.classList.remove('is-open'); setTimeout(() => s.remove(), 250); });
  const openSheet = (src) => {
    closeSheet();
    const it = src.el;
    const from = src.from;
    const drawer = it.closest('fw2-drawer');
    const targets = ownCards(drawer).filter((c) => movable(c) && c.dataset.id !== from);
    const name = src.name || 'this item';
    const sheet = document.createElement('div');
    sheet.className = 'sp-move-sheet';
    sheet.innerHTML = `<div class="sp-move-sheet__backdrop"></div><div class="sp-move-sheet__panel" role="dialog" aria-label="Move to another list">
      <p class="sp-move-sheet__title">Move to…</p><p class="sp-move-sheet__item">${esc(name)}</p>
      ${targets.length ? targets.map((c) => `<button type="button" class="sp-move-sheet__list" data-to="${esc(c.dataset.id)}">${esc(nameOf(c.dataset.id))}</button>`).join('') : '<p class="sp-move-sheet__empty">Create another list to move items between lists.</p>'}
      <button type="button" class="sp-move-sheet__cancel">Cancel</button></div>`;
    document.body.appendChild(sheet);
    requestAnimationFrame(() => sheet.classList.add('is-open'));
    sheet.addEventListener('click', (e) => {
      const t = e.target.closest('.sp-move-sheet__list');
      if (t) { closeSheet(); move(src.vid, from, t.dataset.to); return; }
      if (e.target.closest('.sp-move-sheet__cancel, .sp-move-sheet__backdrop')) closeSheet();
    });
  };
  document.addEventListener('touchstart', (e) => {
    if (e.target.closest && e.target.closest('fw2-item-row button, fw2-item-row select, fw2-item-row a.fw2-btn, .sp-strip__add')) return;
    const src = source(e.target);
    if (!src || !movable(src.el.closest('fw2-list-card'))) return;
    const it = src.el;
    const t = e.touches[0];
    press = { it, x: t.clientX, y: t.clientY, timer: setTimeout(() => { suppressClick = true; it.classList.add('is-pressed'); if (navigator.vibrate) navigator.vibrate(10); openSheet(src); press = null; }, 450) };
  }, { passive: true });
  document.addEventListener('touchmove', (e) => {
    if (!press) return;
    const t = e.touches[0];
    if (Math.abs(t.clientX - press.x) > 8 || Math.abs(t.clientY - press.y) > 8) { clearTimeout(press.timer); press = null; } // it's a swipe
  }, { passive: true });
  ['touchend', 'touchcancel'].forEach((ev) => document.addEventListener(ev, () => {
    if (press) { clearTimeout(press.timer); press = null; }
    document.querySelectorAll('fw2-drawer .is-pressed').forEach((n) => n.classList.remove('is-pressed'));
  }, { passive: true }));
  document.addEventListener('contextmenu', (e) => { if (e.target.closest && e.target.closest('fw2-drawer .sp-strip__item, fw2-drawer fw2-item-row.sp-row-movable')) e.preventDefault(); });
  // a long-press on an open row shouldn't also open the product
  document.addEventListener('click', (e) => {
    if (!suppressClick || !(e.target.closest && e.target.closest('fw2-drawer fw2-item-row'))) return;
    suppressClick = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);

  /* ---------- V3 · quick add from a closed row: the saved size straight into the bag ---------- */
  // goes through window.fetch with Fish's _fish_wishlist property, so wishlist-enhancements.js opens the Bag drawer
  const quickAdd = async (btn) => {
    const it = btn.closest('.sp-strip__item');
    if (!it || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    try {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const r = await window.fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(it.dataset.vid), quantity: 1, properties: { _fish_wishlist: it.dataset.list || 'true' } }] }),
      });
      if (!r.ok) {
        let msg = 'Couldn’t add that to your bag. Please try again.';
        try { const j = await r.json(); if (j && j.description) msg = j.description; } catch (x) { /* keep the default */ }
        toast(msg);
      } else {
        btn.classList.add('is-added');
        setTimeout(() => btn.classList.remove('is-added'), 1500);
      }
    } catch (e) {
      toast('Couldn’t add that to your bag. Please try again.');
    } finally {
      btn.classList.remove('is-loading');
    }
  };

  /* ---------- tap a product in a closed row → its product page (the + adds it to the bag) ---------- */
  document.addEventListener('click', (e) => {
    const it = e.target.closest && e.target.closest('fw2-drawer .sp-strip__item');
    if (!it) return;
    e.preventDefault();
    e.stopPropagation();
    if (suppressClick) { suppressClick = false; return; }
    const add = e.target.closest('.sp-strip__add');
    if (add) { quickAdd(add); return; }
    if (it.dataset.url) window.location.href = it.dataset.url;
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const it = e.target.closest && e.target.closest('fw2-drawer .sp-strip__item');
    if (it && e.target === it && it.dataset.url) window.location.href = it.dataset.url;
  });

  /* ---------- keep the rows in step with Fish ---------- */
  let t = null;
  new MutationObserver((muts) => {
    if (!muts.some((m) => m.target.closest && m.target.closest('fw2-drawer') && !(m.target.closest('.sp-strip')))) return;
    clearTimeout(t);
    t = setTimeout(refresh, 150);
  }).observe(document.documentElement, { subtree: true, childList: true });
  document.addEventListener('click', () => setTimeout(refresh, 400), true);
})();
